import { useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { NumericField, RadioGroup } from '~/components/primitives/Field'
import { useAsyncData } from '~/hooks/useAsyncData'
import { PHASE_LABELS, type PhaseRecord } from '~/models/phase'
import { DESIRED_OUTCOME_LABELS, DESIRED_OUTCOMES, type ProjectRecord } from '~/models/project'
import { isRouteRecommendation } from '~/models/routeRecommendation'
import { ReadingTriple, RouteSummary } from '~/pages/setup/ProjectSetupPage'
import {
  TRAINING_EXPERIENCE_LABELS,
  TRAINING_EXPERIENCE_LEVELS,
  type TrainingExperience,
} from '~/rules/route/snapshot'
import {
  getNextPhaseState,
  type NextPhaseAnswers,
  type NextPhaseRecommendation,
  recommendNextPhase,
  startNextPhase,
} from '~/services/nextPhaseService'
import { setupErrorMessage } from '~/services/projectSetupService'
import { useDatabase } from '~/storage/DatabaseContext'
import { STORE } from '~/storage/schema'
import { formatCalendarDate } from '~/utils/format'
import styles from './NextPhasePage.module.css'

/**
 * Choosing the phase that follows a finished one (D-103 §3, §4, §6, §8).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS NOT SETUP, AND THAT IS THE WHOLE POINT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Before this screen existed, ending a phase left the user on Today being asked
 * to set up Project Sola — and taking that offer wrote a **second active
 * project**, orphaning the first along with its recommendation snapshot, its
 * weekly reviews and its domain goals. Proven in a browser.
 *
 * So this screen creates a phase and nothing else. The project it belongs to is
 * the one already running; no profile is written, no domains are asked about,
 * and completing or abandoning a project stays the explicit decision in Settings
 * (D-020, D-080).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THREE QUESTIONS, ALL ASKED AGAIN ON PURPOSE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The measurement, the training answers and the destination. **A stale waist is
 * not a weaker recommendation — it is a recommendation about someone else**
 * (§4), D-071's bands are waist-to-height, and moving that number was the whole
 * purpose of the phase that just ended. The training answers are asked for the
 * same reason and because nothing stores them; without a session count the
 * engine can never recommend a lean bulk at all.
 *
 * **Only the destination is pre-filled** (§6), and D-093 §6's cost is stated
 * where it applies: a pre-filled field is usually just accepted, which is the
 * intended common case here.
 *
 * There is no start-date field. A handover starts the day after the previous
 * phase ended (D-078) and the service computes it, so the one date a user could
 * get wrong is not offered.
 */

type Step = 'measure' | 'training' | 'destination' | 'confirm'

const STEP_TITLES: Readonly<Record<Step, string>> = {
  measure: 'Where you are now',
  training: 'How you train now',
  destination: 'What you are aiming for',
  confirm: 'What comes next',
}

const STEP_ORDER: readonly Step[] = ['measure', 'training', 'destination', 'confirm']

const EMPTY_ANSWERS: NextPhaseAnswers = {
  waistReadings: ['', '', ''],
  neckReadings: ['', '', ''],
  weeklySessions: '',
  trainingExperience: '',
  desiredOutcome: '',
}

/**
 * The gates, kept apart from the flow.
 *
 * Split for the same reason the review wizard is: the loading, unreadable and
 * nothing-to-decide states have nothing to do with the four steps, and holding
 * both in one component put a write path over the complexity threshold. The flow
 * below is only ever rendered with a project in hand.
 */
export function NextPhasePage() {
  const db = useDatabase()
  const [, navigate] = useLocation()

  const { state } = useAsyncData(async () => getNextPhaseState(db), [STORE.phases, STORE.projects])

  if (state.status === 'loading') {
    return (
      <Screen title="Your next phase">
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="Your next phase">
        <p role="alert">Project Sola could not read your data. {state.message}</p>
      </Screen>
    )
  }

  const available = state.value.ok && state.value.value.available
  const project = state.value.ok ? state.value.value.project : undefined
  const previous = state.value.ok ? state.value.value.previousPhase : undefined

  /* Reached with nothing to decide — a bookmark, a back button, or a phase
     started in another tab. It says so and offers the way out rather than
     showing an empty form. */
  if (!available || project === undefined) {
    return (
      <Screen title="Your next phase">
        <p class={styles.lede}>
          There is no finished phase to follow just now, so there is nothing to choose. Your records
          are unaffected.
        </p>
        <Button onClick={() => navigate('/today')}>Back to Today</Button>
      </Screen>
    )
  }

  return <NextPhaseFlow project={project} previous={previous} />
}

function NextPhaseFlow({
  project,
  previous,
}: {
  project: ProjectRecord
  previous: PhaseRecord | undefined
}) {
  const db = useDatabase()
  const [, navigate] = useLocation()

  const [step, setStep] = useState<Step>('measure')
  const [answers, setAnswers] = useState<NextPhaseAnswers>(EMPTY_ANSWERS)
  const [recommendation, setRecommendation] = useState<NextPhaseRecommendation | undefined>(
    undefined,
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  /* The destination is pre-filled from the project once, when it arrives. Doing
     it on every render would overwrite an edit the user just made. */
  const [prefilled, setPrefilled] = useState(false)

  if (!prefilled && project.desiredOutcome !== undefined) {
    setPrefilled(true)
    setAnswers((current) => ({ ...current, desiredOutcome: project.desiredOutcome as string }))
  }

  const index = STEP_ORDER.indexOf(step)

  function update(patch: Partial<NextPhaseAnswers>) {
    setAnswers((current) => ({ ...current, ...patch }))
    setError(undefined)
  }

  async function goForward() {
    setError(undefined)

    const problem = validateStep(step, answers)
    if (problem !== undefined) {
      setError(problem)
      return
    }

    if (step !== 'destination') {
      setStep(STEP_ORDER[index + 1] as Step)
      return
    }

    /* The engine runs when the last answer is in, and produces a value. Nothing
       is written by this (D-020) — the confirmation below is the act. */
    setBusy(true)
    const result = await recommendNextPhase(db, answers, new Date())
    setBusy(false)

    if (!result.ok) {
      setError(setupErrorMessage(result.error))
      return
    }
    setRecommendation(result.value)
    setStep('confirm')
  }

  async function start() {
    if (recommendation === undefined) return
    setBusy(true)
    setError(undefined)
    const result = await startNextPhase(db, { answers, recommendation }, new Date())
    setBusy(false)

    if (!result.ok) {
      setError(setupErrorMessage(result.error))
      return
    }
    navigate('/today')
  }

  return (
    <main id="main-content" class={styles.screen} tabIndex={-1}>
      <p class={styles.progress} aria-live="polite">
        {step === 'confirm'
          ? 'Your next phase'
          : `Step ${index + 1} of ${STEP_ORDER.length - 1} — ${STEP_TITLES[step]}`}
      </p>

      {previous === undefined ? null : (
        <p class={styles.previous}>
          {PHASE_LABELS[previous.type]} ended on{' '}
          {formatCalendarDate(previous.actualEndDate ?? previous.plannedEndDate)}. Your project
          continues.
        </p>
      )}

      <StepBody step={step} answers={answers} recommendation={recommendation} onChange={update} />

      {error === undefined ? null : (
        <p class={styles.problem} role="alert">
          {error}
        </p>
      )}

      {/* Accept, adjust, and leave — all three, on the screen that asks for a
          commitment (D-020).

          Found on a device: "Not now" rendered only on the first step, so from
          the recommendation the only ways out were three Back presses or the
          operating system's own gesture — the tab bar is suppressed here, as it
          is during Project Setup. A screen that says "Nothing has been created
          yet" has to offer a labelled way to keep it that way.

          `PhaseHistorySection` settled this one level up: "Leaving it alone is a
          real choice, so it is a control rather than the absence of one." Not
          deciding yet is a legitimate answer and needs a button, not a gesture.

          Every other step keeps two, because Back IS the way out of step one and
          a third control there would be the same action twice. */}
      <div class={styles.actions}>
        {index > 0 ? (
          <Button
            variant="secondary"
            onClick={() => {
              setError(undefined)
              setStep(STEP_ORDER[index - 1] as Step)
            }}
          >
            Back
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => navigate('/today')}>
            Not now
          </Button>
        )}

        {step === 'confirm' ? (
          <Button variant="secondary" onClick={() => navigate('/today')}>
            Not now
          </Button>
        ) : null}

        {step === 'confirm' ? (
          <Button onClick={() => void start()} busy={busy} busyLabel="Starting…">
            Start this phase
          </Button>
        ) : (
          <Button onClick={() => void goForward()} busy={busy} busyLabel="Working…">
            Continue
          </Button>
        )}
      </div>
    </main>
  )
}

interface StepProps {
  answers: NextPhaseAnswers
  onChange: (patch: Partial<NextPhaseAnswers>) => void
}

function StepBody({
  step,
  answers,
  recommendation,
  onChange,
}: StepProps & { step: Step; recommendation: NextPhaseRecommendation | undefined }) {
  if (step === 'measure') return <MeasureStep answers={answers} onChange={onChange} />
  if (step === 'training') return <TrainingStep answers={answers} onChange={onChange} />
  if (step === 'destination') return <DestinationStep answers={answers} onChange={onChange} />
  if (recommendation === undefined) return null
  return <ConfirmStep recommendation={recommendation} />
}

function MeasureStep({ answers, onChange }: StepProps) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        {STEP_TITLES.measure}
      </h1>
      <p class={styles.lede}>
        Measure again before Project Sola advises. The recommendation rests on your waist as a
        proportion of your height, and that is the number the last phase was about.
      </p>
      <p class={styles.hint}>
        Three readings of each site. Project Sola saves the middle one and tells you if they
        disagree by more than it expects.
      </p>

      <ReadingTriple
        label="Waist"
        readings={answers.waistReadings}
        onChange={(waistReadings) => onChange({ waistReadings })}
      />
      <ReadingTriple
        label="Neck"
        readings={answers.neckReadings}
        onChange={(neckReadings) => onChange({ neckReadings })}
      />
    </section>
  )
}

function TrainingStep({ answers, onChange }: StepProps) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        {STEP_TITLES.training}
      </h1>
      <p class={styles.lede}>
        Both of these change what Project Sola can recommend, and both may have moved since you
        started.
      </p>

      <RadioGroup
        legend="How long have you been training consistently?"
        value={
          TRAINING_EXPERIENCE_LEVELS.includes(answers.trainingExperience as TrainingExperience)
            ? (answers.trainingExperience as TrainingExperience)
            : undefined
        }
        onChange={(trainingExperience) => onChange({ trainingExperience })}
        options={TRAINING_EXPERIENCE_LEVELS.map((level) => ({
          value: level,
          label: TRAINING_EXPERIENCE_LABELS[level],
        }))}
      />

      {/* The one that decides whether a lean bulk is reachable at all. Without
          it `applyTrainingFrequencyFloor` falls back to maintenance and says so,
          which is honest but is not the answer someone who trains four times a
          week should get. */}
      <NumericField
        label="Sessions a week you realistically train"
        hint="Be honest rather than optimistic. A lean bulk needs enough training to use the extra food, so this decides whether one is on the table."
        placeholder="3"
        value={answers.weeklySessions}
        onInput={(weeklySessions) => onChange({ weeklySessions })}
      />
    </section>
  )
}

/**
 * The destination, pre-filled and editable (D-103 §6).
 *
 * **The cost is stated rather than buried**, and it is D-093 §6's in a second
 * costume: a pre-filled field is usually just accepted, so in practice the
 * destination carries forward. That is the intended common case; the edit exists
 * for the user who genuinely wants something else, and forcing them into a new
 * project for it would end this one by side effect.
 */
function DestinationStep({ answers, onChange }: StepProps) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        {STEP_TITLES.destination}
      </h1>
      <p class={styles.lede}>
        Carried over from your project. Change it if you want something different now — your project
        keeps running either way, and the change is recorded.
      </p>

      <RadioGroup
        legend="Desired outcome"
        value={answers.desiredOutcome === '' ? undefined : answers.desiredOutcome}
        onChange={(desiredOutcome) => onChange({ desiredOutcome })}
        options={DESIRED_OUTCOMES.map((outcome) => ({
          value: outcome,
          label: DESIRED_OUTCOME_LABELS[outcome],
        }))}
      />
    </section>
  )
}

function ConfirmStep({ recommendation }: { recommendation: NextPhaseRecommendation }) {
  const { outcome, startDate, outlook } = recommendation

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        {STEP_TITLES.confirm}
      </h1>

      {isRouteRecommendation(outcome) ? (
        <>
          <RouteSummary recommendation={outcome} outlook={outlook} />
          <p class={styles.starts}>
            Starts {formatCalendarDate(startDate)}, the day after your last phase ended.
          </p>
        </>
      ) : (
        <p class={styles.lede}>
          Project Sola cannot recommend a phase from what it can see right now, so it is not
          guessing at one. Nothing has changed.
        </p>
      )}

      {/* D-020, and the sentence the previous flow could not honestly say. */}
      <p class={styles.notice}>
        <strong>Nothing has been created yet.</strong> Your project, your history and your records
        are exactly as they were until you press the button below.
      </p>
    </section>
  )
}

/** Per-step validation. The service validates again; this catches it earlier. */
function validateStep(step: Step, answers: NextPhaseAnswers): string | undefined {
  if (step === 'measure') {
    const filled = (readings: readonly string[]) =>
      readings.filter((value) => value.trim() !== '').length
    if (filled(answers.waistReadings) < answers.waistReadings.length) {
      return 'Enter all three waist readings.'
    }
    if (filled(answers.neckReadings) < answers.neckReadings.length) {
      return 'Enter all three neck readings.'
    }
    return undefined
  }

  if (step === 'training') {
    if (answers.trainingExperience === '') return 'Choose how long you have been training.'
    const sessions = Number(answers.weeklySessions)
    if (answers.weeklySessions.trim() === '' || !Number.isFinite(sessions) || sessions < 0) {
      return 'Enter how many sessions a week you realistically train.'
    }
    return undefined
  }

  if (step === 'destination') {
    if (answers.desiredOutcome === '') return 'Choose what you are aiming for next.'
    return undefined
  }

  return undefined
}
